// Sidebar sections: your own sections (like Slack's custom sections) plus the built-in
// "Channels" and "Direct messages" catch-alls. Slack doesn't expose its own sections to
// third-party apps, so Assembly keeps this layout itself and syncs it through the server
// (state/sidebar.ts). Everything here is pure and unit tested (layout.test.ts).

import type { Conversation } from '@assembly/slack'

export type SectionSort = 'alpha' | 'recent' | 'manual'
export type BuiltinId = 'channels' | 'dms'

export interface CustomSection {
  id: string
  name: string
  emoji?: string
  sort: SectionSort
  collapsed?: boolean
  /** Conversation ids, in manual order. A conversation is in at most one section. */
  items: string[]
}

export interface BuiltinSection {
  sort: Exclude<SectionSort, 'manual'>
  collapsed?: boolean
  /** Direct messages only: show conversations active in the last N days (0 = all), like Slack. */
  days?: number
}

export const DM_WINDOWS = [1, 7, 30, 0] as const
export const DEFAULT_DM_DAYS = 30
const DAY = 86_400_000

export interface SidebarLayout {
  v: 1
  /** Last local edit (ms); the newer copy wins when devices disagree. */
  updatedAt: number
  sections: CustomSection[]
  builtins: Record<BuiltinId, BuiltinSection>
  /** Display order of every section id, custom and built-in. */
  order: string[]
}

export const STARRED_ID = 'starred'
export const BUILTIN_IDS: BuiltinId[] = ['channels', 'dms']
export const isBuiltin = (id: string): id is BuiltinId => id === 'channels' || id === 'dms'
const MAX_SECTIONS = 50
const MAX_NAME = 80

export function emptyLayout(): SidebarLayout {
  return { v: 1, updatedAt: 0, sections: [], builtins: { channels: { sort: 'alpha' }, dms: { sort: 'recent', days: DEFAULT_DM_DAYS } }, order: ['channels', 'dms'] }
}

/** Repairs anything malformed (old versions, hand edits, partial syncs) into a valid layout. */
export function normalize(raw: unknown): SidebarLayout {
  const base = emptyLayout()
  if (!raw || typeof raw !== 'object') return base
  const r = raw as Partial<SidebarLayout>
  const seenItems = new Set<string>()
  const seenIds = new Set<string>()
  const sections: CustomSection[] = []
  for (const s of Array.isArray(r.sections) ? r.sections : []) {
    if (!s || typeof s !== 'object' || typeof s.id !== 'string' || !s.id || isBuiltin(s.id) || seenIds.has(s.id)) continue
    if (sections.length >= MAX_SECTIONS) break
    seenIds.add(s.id)
    const items: string[] = []
    for (const i of Array.isArray(s.items) ? s.items : []) {
      if (typeof i !== 'string' || seenItems.has(i)) continue
      seenItems.add(i)
      items.push(i)
    }
    sections.push({
      id: s.id,
      name: typeof s.name === 'string' && s.name.trim() ? s.name.trim().slice(0, MAX_NAME) : 'Untitled',
      ...(typeof s.emoji === 'string' && s.emoji ? { emoji: s.emoji } : {}),
      sort: s.sort === 'alpha' || s.sort === 'recent' || s.sort === 'manual' ? s.sort : 'manual',
      ...(s.collapsed ? { collapsed: true } : {}),
      items,
    })
  }
  const builtins = { ...base.builtins }
  for (const id of BUILTIN_IDS) {
    const b = r.builtins?.[id]
    if (b && typeof b === 'object') builtins[id] = { sort: b.sort === 'alpha' || b.sort === 'recent' ? b.sort : base.builtins[id].sort, ...(b.collapsed ? { collapsed: true } : {}) }
  }
  // Layouts saved before the DM window existed get Slack's default.
  const days = (r.builtins?.dms as BuiltinSection | undefined)?.days
  builtins.dms = { ...builtins.dms, days: typeof days === 'number' && (DM_WINDOWS as readonly number[]).includes(days) ? days : DEFAULT_DM_DAYS }
  const valid = new Set([...sections.map((s) => s.id), ...BUILTIN_IDS])
  const order = [...new Set((Array.isArray(r.order) ? r.order : []).filter((id): id is string => typeof id === 'string' && valid.has(id)))]
  // Anything missing from the order: custom sections go before the built-ins, like Slack.
  for (const s of sections) {
    if (order.includes(s.id)) continue
    const firstBuiltin = order.findIndex(isBuiltin)
    order.splice(firstBuiltin === -1 ? order.length : firstBuiltin, 0, s.id)
  }
  for (const id of BUILTIN_IDS) if (!order.includes(id)) order.push(id)
  return { v: 1, updatedAt: typeof r.updatedAt === 'number' && Number.isFinite(r.updatedAt) ? r.updatedAt : 0, sections, builtins, order }
}

const touch = (l: SidebarLayout, now: number): SidebarLayout => ({ ...l, updatedAt: Math.max(now, l.updatedAt + 1) })

export function newSectionId(name: string, existing: string[], random = Math.random): string {
  const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 24) || 'section'
  let id = slug
  while (existing.includes(id) || isBuiltin(id) || id === STARRED_ID) id = `${slug}-${random().toString(36).slice(2, 6)}`
  return id
}

export function sectionOf(l: SidebarLayout, convId: string): string | null {
  return l.sections.find((s) => s.items.includes(convId))?.id ?? null
}

// ------------------------------------------------------------------ edits (each returns a new layout)

export function createSection(l: SidebarLayout, name: string, opts: { emoji?: string; items?: string[]; id?: string; now?: number } = {}): { layout: SidebarLayout; id: string } {
  const trimmed = name.trim().slice(0, MAX_NAME)
  if (!trimmed) throw new Error('Section name is required')
  if (l.sections.length >= MAX_SECTIONS) throw new Error(`You can have up to ${MAX_SECTIONS} sections`)
  const id = opts.id ?? newSectionId(trimmed, l.sections.map((s) => s.id))
  const items = opts.items ?? []
  const sections = l.sections.map((s) => ({ ...s, items: s.items.filter((i) => !items.includes(i)) }))
  sections.push({ id, name: trimmed, ...(opts.emoji ? { emoji: opts.emoji } : {}), sort: 'manual', items })
  // New sections go just above the built-ins.
  const firstBuiltin = l.order.findIndex(isBuiltin)
  const order = [...l.order]
  order.splice(firstBuiltin === -1 ? order.length : firstBuiltin, 0, id)
  return { layout: touch({ ...l, sections, order }, opts.now ?? Date.now()), id }
}

export function renameSection(l: SidebarLayout, id: string, name: string, emoji?: string | null, now = Date.now()): SidebarLayout {
  const trimmed = name.trim().slice(0, MAX_NAME)
  if (!trimmed) throw new Error('Section name is required')
  return touch(
    {
      ...l,
      sections: l.sections.map((s) => {
        if (s.id !== id) return s
        const next: CustomSection = { ...s, name: trimmed }
        if (emoji === null || emoji === '') delete next.emoji
        else if (emoji !== undefined) next.emoji = emoji
        return next
      }),
    },
    now,
  )
}

/** Deletes a custom section; its conversations fall back to Channels / Direct messages. */
export function deleteSection(l: SidebarLayout, id: string, now = Date.now()): SidebarLayout {
  if (isBuiltin(id)) return l
  return touch({ ...l, sections: l.sections.filter((s) => s.id !== id), order: l.order.filter((o) => o !== id) }, now)
}

export function setCollapsed(l: SidebarLayout, id: string, collapsed: boolean, now = Date.now()): SidebarLayout {
  if (isBuiltin(id)) {
    const b = { ...l.builtins[id] }
    if (collapsed) b.collapsed = true
    else delete b.collapsed
    return touch({ ...l, builtins: { ...l.builtins, [id]: b } }, now)
  }
  const sections = l.sections.map((s) => {
    if (s.id !== id) return s
    const next = { ...s }
    if (collapsed) next.collapsed = true
    else delete next.collapsed
    return next
  })
  return touch({ ...l, sections }, now)
}

export function setSort(l: SidebarLayout, id: string, sort: SectionSort, now = Date.now()): SidebarLayout {
  if (isBuiltin(id)) return sort === 'manual' ? l : touch({ ...l, builtins: { ...l.builtins, [id]: { ...l.builtins[id], sort } } }, now)
  return touch({ ...l, sections: l.sections.map((s) => (s.id === id ? { ...s, sort } : s)) }, now)
}

/** How far back the built-in Direct messages section goes (0 = everything). */
export function setDmWindow(l: SidebarLayout, days: number, now = Date.now()): SidebarLayout {
  if (!(DM_WINDOWS as readonly number[]).includes(days) || l.builtins.dms.days === days) return l
  return touch({ ...l, builtins: { ...l.builtins, dms: { ...l.builtins.dms, days } } }, now)
}

/** Moves a section (custom or built-in) to a new position in the sidebar. */
export function moveSection(l: SidebarLayout, id: string, toIndex: number, now = Date.now()): SidebarLayout {
  const from = l.order.indexOf(id)
  if (from === -1) return l
  const order = l.order.filter((o) => o !== id)
  order.splice(Math.max(0, Math.min(toIndex > from ? toIndex - 1 : toIndex, order.length)), 0, id)
  return order.join() === l.order.join() ? l : touch({ ...l, order }, now)
}

/**
 * Moves a conversation into a section (at `index` within its items, else the end).
 * Moving to a built-in removes it from custom sections, so it shows under its default.
 */
export function moveConversation(l: SidebarLayout, convId: string, toSection: string, index?: number, now = Date.now()): SidebarLayout {
  const target = l.sections.find((s) => s.id === toSection)
  if (!target && !isBuiltin(toSection)) return l
  const sections = l.sections.map((s) => {
    const items = s.items.filter((i) => i !== convId)
    if (s.id !== toSection) return items.length === s.items.length ? s : { ...s, items }
    const at = index === undefined ? items.length : Math.max(0, Math.min(index, items.length))
    items.splice(at, 0, convId)
    return { ...s, items }
  })
  return touch({ ...l, sections }, now)
}

export const isStarred = (l: SidebarLayout, convId: string) => !!l.sections.find((s) => s.id === STARRED_ID)?.items.includes(convId)

/** Stars a conversation: moves it to a "Starred" section at the top, created on first use. */
export function star(l: SidebarLayout, convId: string, now = Date.now()): SidebarLayout {
  let layout = l
  if (!layout.sections.some((s) => s.id === STARRED_ID)) {
    layout = createSection(layout, 'Starred', { id: STARRED_ID, emoji: 'star', now }).layout
    layout = moveSection(layout, STARRED_ID, 0, now)
  }
  return moveConversation(layout, convId, STARRED_ID, undefined, now)
}

/** Takes a conversation out of Starred; it goes back to Channels / Direct messages. */
export function unstar(l: SidebarLayout, convId: string, now = Date.now()): SidebarLayout {
  // Moving to any built-in just removes it from custom sections; it's then placed by type.
  return isStarred(l, convId) ? moveConversation(l, convId, 'channels', undefined, now) : l
}

// ------------------------------------------------------------------ rendering

export interface SidebarGroup {
  id: string
  name: string
  emoji?: string
  builtin: boolean
  sort: SectionSort
  collapsed: boolean
  conversations: Conversation[]
  /** Unread conversations hidden by a collapsed section (Slack still shows those). */
  unreadCount: number
}

export interface BuildContext {
  name: (c: Conversation) => string
  /** Last activity in ms, for "recent" sorting and the Direct messages time window. */
  activity: (c: Conversation) => number
  isUnread: (c: Conversation) => boolean
  /** Always shown even when filtering (the open conversation). */
  current?: string | null
  unreadsOnly?: boolean
  /** For the Direct messages time window (ms). */
  now?: number
  /** When the conversation's last message was posted (ms), if known; the DM window uses this over `activity`. */
  lastMessageAt?: (c: Conversation) => number | undefined
}

const isDirect = (c: Conversation) => !!(c.is_im || c.is_mpim)

/** Groups conversations into the layout's sections, sorted, for the sidebar to render. */
export function buildSidebar(l: SidebarLayout, convs: Conversation[], ctx: BuildContext): SidebarGroup[] {
  const byId = new Map(convs.map((c) => [c.id, c] as const))
  const placed = new Set<string>()
  const sortList = (list: Conversation[], sort: SectionSort) => {
    if (sort === 'manual') return list
    const byName = (a: Conversation, b: Conversation) => ctx.name(a).localeCompare(ctx.name(b), undefined, { sensitivity: 'base' })
    return [...list].sort(sort === 'alpha' ? byName : (a, b) => ctx.activity(b) - ctx.activity(a) || byName(a, b))
  }
  const visible = (c: Conversation) => !ctx.unreadsOnly || ctx.isUnread(c) || c.id === ctx.current

  const groups = new Map<string, SidebarGroup>()
  for (const s of l.sections) {
    const list = s.items.map((id) => byId.get(id)).filter((c): c is Conversation => !!c)
    list.forEach((c) => placed.add(c.id))
    groups.set(s.id, groupOf(s.id, s.name, s.emoji, false, s.sort, !!s.collapsed, sortList(list, s.sort)))
  }
  const rest = convs.filter((c) => !placed.has(c.id))
  // Like Slack, the built-in DM list only shows recent conversations (plus unread and open ones).
  // DMs you put in your own sections or starred always show; older ones are a ⌘K away.
  const days = l.builtins.dms.days ?? DEFAULT_DM_DAYS
  const since = (ctx.now ?? Date.now()) - days * DAY
  const recentEnough = (c: Conversation) => {
    if (days === 0 || ctx.isUnread(c) || c.id === ctx.current) return true
    // Prefer the real last-message time; Slack's `updated` also moves for non-message changes.
    return (ctx.lastMessageAt?.(c) ?? ctx.activity(c)) >= since
  }
  groups.set('channels', groupOf('channels', 'Channels', undefined, true, l.builtins.channels.sort, !!l.builtins.channels.collapsed, sortList(rest.filter((c) => !isDirect(c)), l.builtins.channels.sort)))
  groups.set('dms', groupOf('dms', 'Direct messages', undefined, true, l.builtins.dms.sort, !!l.builtins.dms.collapsed, sortList(rest.filter((c) => isDirect(c) && recentEnough(c)), l.builtins.dms.sort)))

  function groupOf(id: string, name: string, emoji: string | undefined, builtin: boolean, sort: SectionSort, collapsed: boolean, list: Conversation[]): SidebarGroup {
    const shown = list.filter(visible)
    return {
      id,
      name,
      ...(emoji ? { emoji } : {}),
      builtin,
      sort,
      collapsed,
      // Collapsed sections still show unread conversations and the open one, like Slack.
      conversations: collapsed ? shown.filter((c) => ctx.isUnread(c) || c.id === ctx.current) : shown,
      unreadCount: collapsed ? list.filter((c) => ctx.isUnread(c)).length : 0,
    }
  }

  return l.order.map((id) => groups.get(id)).filter((g): g is SidebarGroup => !!g)
}

/**
 * Adds AI-proposed sections. Channels go into an existing section with the same name
 * (case-insensitive) when there is one, otherwise a new section is created.
 */
export function applyProposals(l: SidebarLayout, proposals: { name: string; emoji?: string; channels: string[] }[], now = Date.now()): SidebarLayout {
  let layout = l
  for (const p of proposals) {
    const existing = layout.sections.find((x) => x.name.toLowerCase() === p.name.trim().toLowerCase())
    if (existing) for (const id of p.channels) layout = moveConversation(layout, id, existing.id, undefined, now)
    else if (layout.sections.length < MAX_SECTIONS) layout = createSection(layout, p.name, { emoji: p.emoji, items: p.channels, now }).layout
  }
  return layout
}

/** Last-writer-wins merge between this device's layout and the server's. */
export function newer(local: SidebarLayout, remote: SidebarLayout | null): SidebarLayout {
  return remote && remote.updatedAt > local.updatedAt ? remote : local
}
